export type Icp = 'default' | 'agency' | 'it' | 'bpo' | 'status';

interface QueryParamGetter {
  get(name: string): string | null;
}

const ICP_STORAGE_KEY = 'ss_signup_icp';
const REMEMBERED_ICPS: Icp[] = ['agency', 'it', 'bpo', 'status'];

/**
 * Picks which buyer the signup page should speak to.
 * The landing pages and ads can pass an explicit `?icp=agency|it|bpo|status`; otherwise the first-touch page the
 * website already sends as `utm_ft_page` is used when it names one of the industry pages, and the
 * "Kya Status Hai" ad is recognised from its name. Anything else falls back to the general message.
 */
export function resolveIcp(queryParams: QueryParamGetter): Icp {
  const explicit = (queryParams.get('icp') || '').trim().toLowerCase();
  if (explicit === 'agency' || explicit === 'agencies') {
    return 'agency';
  }
  if (explicit === 'it') {
    return 'it';
  }
  if (explicit === 'bpo') {
    return 'bpo';
  }
  if (explicit === 'status') {
    return 'status';
  }

  // The "Kya Status Hai" Meta ad: recognised from its name in utm_content / utm_campaign (punctuation ignored)
  const adName = `${queryParams.get('utm_content') || ''} ${queryParams.get('utm_campaign') || ''}`
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
  if (adName.includes('kyastatus')) {
    return 'status';
  }

  const page = (queryParams.get('utm_ft_page') || '').trim().toLowerCase();
  if (page.includes('digital-agencies')) {
    return 'agency';
  }
  if (page.includes('it-industry')) {
    return 'it';
  }
  if (page.includes('bpo')) {
    return 'bpo';
  }

  return 'default';
}

/**
 * Same as resolveIcp, but remembers the choice for the rest of the browser tab, so a refresh or a trip to the
 * login page and back does not change the headline the visitor first saw.
 */
export function resolveIcpForVisit(queryParams: QueryParamGetter, storage?: Storage | null): Icp {
  const fromUrl = resolveIcp(queryParams);
  try {
    if (fromUrl !== 'default') {
      storage?.setItem(ICP_STORAGE_KEY, fromUrl);
      return fromUrl;
    }
    const saved = storage?.getItem(ICP_STORAGE_KEY) as Icp | null | undefined;
    if (saved && REMEMBERED_ICPS.includes(saved)) {
      return saved;
    }
  } catch {
    /* storage can be unavailable (private mode); the URL result is still correct */
  }
  return fromUrl;
}
